'use client';

import React, { useEffect, useState } from 'react';
import { motion, useMotionValue, useSpring, useTransform } from 'framer-motion';
import { useInView } from 'framer-motion';

interface AnimatedNumberProps {
  value: number;
  suffix?: string;
  prefix?: string;
  duration?: number;
  triggerOnViewport?: boolean;
  className?: string;
  decimals?: number;
}

const AnimatedNumber: React.FC<AnimatedNumberProps> = ({
  value,
  suffix = '',
  prefix = '',
  duration = 2000,
  triggerOnViewport = true,
  className = '',
  decimals = 0,
}) => {
  const ref = React.useRef<HTMLSpanElement>(null);
  const isInView = useInView(ref, { once: true, amount: 0.2 });
  const [hasAnimated, setHasAnimated] = useState(false);

  const motionValue = useMotionValue(0);
  const spring = useSpring(motionValue, {
    damping: 60,
    stiffness: 100,
  });

  const displayValue = useTransform(spring, (latest) => {
    const num = decimals > 0 ? latest.toFixed(decimals) : Math.floor(latest).toString();
    return num.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  });

  useEffect(() => {
    if (triggerOnViewport && !isInView) return;
    if (hasAnimated && triggerOnViewport) return;

    motionValue.set(value);
    setHasAnimated(true);
  }, [value, isInView, triggerOnViewport, hasAnimated, motionValue]);

  return (
    <span data-component-id="src/components/motion/AnimatedNumber.tsx:50:4" data-component-path="src/components/motion/AnimatedNumber.tsx" data-component-line="50" data-component-end-line="54" data-component-file="AnimatedNumber.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22%5Bvar%3AclassName%5D%22%7D" ref={ref} className={className}>
      {prefix}
      <motion.span data-component-id="src/components/motion/AnimatedNumber.tsx:52:6" data-component-path="src/components/motion/AnimatedNumber.tsx" data-component-line="52" data-component-end-line="52" data-component-file="AnimatedNumber.tsx" data-component-name="motion.span" data-component-content="%7B%22elementName%22%3A%22motion.span%22%7D">{displayValue}</motion.span>
      {suffix}
    </span>
  );
};

export default AnimatedNumber;
